import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { SlotTariffa } from "./SlotTariffa";
import * as yup from "yup";
import { queryClient } from "../App";
import { useMutation, useQuery } from "react-query";
import { useSnackbar } from "notistack";
import axios, { AxiosError } from "axios";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { API_URL } from "../consts";
import Grid from "@mui/material/Grid";
import { DatePicker } from "@mui/x-date-pickers";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { Colori, Tariffe_Definizioni } from "../prismaTypes";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useEffect } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { changeDatesToUTC } from "../utils/dateUtils";
import { gruppaOrari, Orari } from "../utils/gruppaOrari";

const CHECK_SIZE = "small";

type Inputs = {
  risorseId: number;
  tariffe_DefinizioniId: number;
  dataInizio: Date | string | null;
  dataFine: Date | string | null;
  lunedi: boolean;
  martedi: boolean;
  mercoledi: boolean;
  giovedi: boolean;
  venerdi: boolean;
  sabato: boolean;
  domenica: boolean;
  Tariffe_Calendario_Orario: Orari[];
};

const schema = yup
  .object({
    dataInizio: yup
      .date()
      .typeError("Data non valida!")
      .nullable()
      .min(new Date(2000, 0, 0, 0, 0))
      .label("Data Inizio")
      .required()
      .default(undefined)
      .transform((curr, orig) => (orig === "" ? null : curr)),
    dataFine: yup
      .date()
      .typeError("Data non valida!")
      .nullable()
      .min(yup.ref("dataInizio"))
      .label("Data Fine")
      .required()
      .default(undefined)
      .transform((curr, orig) => (orig === "" ? null : curr)),
  })
  .required();

export function ImpostaTariffaDialog(props: {
  open: boolean;
  handleClose: () => void;
  risorseId: number;
  slotsTariffe: { [k: string]: SlotTariffa };
  setSlotsTariffe: (val: { [k: string]: SlotTariffa }) => void;
}) {
  const { open, handleClose, slotsTariffe, risorseId, setSlotsTariffe } = props;
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { mutate } = useMutation(
    (data: Inputs) => {
      return axios.post(API_URL + "tariffe-calendario/create", data);
    },
    {
      onSuccess: (data) => {
        if (Array.isArray(data.data)) {
          queryClient.invalidateQueries();
          enqueueSnackbar("Tariffa Impostata", { variant: "success" });
          setSlotsTariffe({});
          handleClose();
        } else enqueueSnackbar("Si è verificato un errore!", { variant: "error" });
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { isLoading, data: tariffe } = useQuery<
    unknown,
    AxiosError,
    {
      definizioni: (Tariffe_Definizioni & {
        colore: Colori;
      })[];
    }
  >(
    ["tariffe-definizioni"],
    () => axios.get(API_URL + "tariffe-definizioni/findAll").then((res) => res.data),
    { refetchOnMount: false, refetchOnWindowFocus: false }
  );

  const {
    control,

    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Inputs>({
    mode: "all",
    resolver: yupResolver(schema),
  });
  const onSubmit = (data: Inputs) => {
    changeDatesToUTC(data);

    const Tariffe_Calendario_Orario: Orari[] = gruppaOrari(slotsTariffe);

    mutate({ ...data, risorseId, Tariffe_Calendario_Orario });
  };

  const values = Object.values(slotsTariffe);

  useEffect(() => {
    const oggi = values[0]?.giorno;
    if (oggi) {
      setValue("dataInizio", oggi);
    }
  }, [values, setValue]);

  if (isLoading) return <></>;

  return (
    <Dialog open={open} onClose={handleClose} fullScreen={fullScreen}>
      <DialogTitle>Imposta Tariffa</DialogTitle>

      {values.length > 0 && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <DialogContent>
              <Grid container columnSpacing={1}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="dataInizio"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        mask={"__/__/____"}
                        label="Inizio"
                        onChange={field.onChange}
                        value={field.value}
                        renderInput={(params) => (
                          <TextField
                            sx={{ mr: 2 }}
                            required
                            variant="standard"
                            helperText={errors.dataInizio?.message ?? " "}
                            {...params}
                            error={Boolean(errors.dataInizio)}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="dataFine"
                    defaultValue=""
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        mask={"__/__/____"}
                        label="Fine"
                        onChange={field.onChange}
                        value={field.value}
                        renderInput={(params) => (
                          <TextField
                            sx={{ mr: 2 }}
                            required
                            variant="standard"
                            helperText={errors.dataFine?.message ?? " "}
                            {...params}
                            error={Boolean(errors.dataFine)}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={10} lg={9}>
                  <Controller
                    name="tariffe_DefinizioniId"
                    control={control}
                    defaultValue={tariffe?.definizioni[0]?.id}
                    render={({ field }) => (
                      <FormControl
                        {...field}
                        fullWidth
                        required
                        variant="standard"
                        error={Boolean(errors.tariffe_DefinizioniId)}
                      >
                        <InputLabel>Tariffa</InputLabel>
                        <Select
                          label="Colore"
                          value={field.value}
                          onChange={(e) =>
                            setValue("tariffe_DefinizioniId", parseInt("" + e.target.value))
                          }
                        >
                          {tariffe?.definizioni?.map((col) => (
                            <MenuItem
                              key={col.id}
                              value={col.id}
                              sx={{ bgcolor: col.colore.esadecimale }}
                            >
                              {col.nome}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {errors.tariffe_DefinizioniId?.message ?? " "}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Controller
                    name="lunedi"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            size={CHECK_SIZE}
                            onChange={(v) => setValue("lunedi", v.target.checked)}
                          />
                        }
                        label="Lunedì"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Controller
                    name="martedi"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            size={CHECK_SIZE}
                            onChange={(v) => setValue("martedi", v.target.checked)}
                          />
                        }
                        label="Martedì"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Controller
                    name="mercoledi"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            size={CHECK_SIZE}
                            onChange={(v) => setValue("mercoledi", v.target.checked)}
                          />
                        }
                        label="Mercoledì"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Controller
                    name="giovedi"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            size={CHECK_SIZE}
                            onChange={(v) => setValue("giovedi", v.target.checked)}
                          />
                        }
                        label="Giovedì"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Controller
                    name="venerdi"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            size={CHECK_SIZE}
                            onChange={(v) => setValue("venerdi", v.target.checked)}
                          />
                        }
                        label="Venerdì"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Controller
                    name="sabato"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            size={CHECK_SIZE}
                            onChange={(v) => setValue("sabato", v.target.checked)}
                          />
                        }
                        label="Sabato"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Controller
                    name="domenica"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            size={CHECK_SIZE}
                            onChange={(v) => setValue("domenica", v.target.checked)}
                          />
                        }
                        label="Domenica"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit">Imposta</Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}
